// ─── Looker table totals ──────────────────────────────────────────────────────
// A Looker table's COLUMN totals never arrive as an extra row in `data`: the
// json_detail result carries them in a separate `totals_data` block. A pivoted
// table's ROW totals arrive as an extra `$$$_row_total_$$$` key inside each
// measure cell (and inside `totals_data` for the grand total).
//
// Imported tiles have always carried the toggles that make Looker compute them
// (`query.total` / `query.row_total`), so the numbers were coming back on every
// run — the table renderer simply never read them, which is how a Looker table
// with a Total row came into Pulse without one.
//
// Pure helpers, no React, so the rules are unit-tested (test/tableTotals.test.js)
// rather than described in a comment.

import { formatNumber, isNumericValue } from './format.js';

export const ROW_TOTAL_KEY = '$$$_row_total_$$$';

// A LEAF cell ({ value, rendered, links }) as opposed to a pivoted cell MAP
// ({ pivotKey: leafCell, … }), which is what a measure holds on a pivoted query.
export function isLeafCell(c) {
  return !!c && typeof c === 'object' && !Array.isArray(c) && ('value' in c || 'rendered' in c || 'rendered_value' in c);
}

// Whether to draw the totals row. `totals_data` only exists when the query asked
// for totals, so its presence IS Looker's answer — but the table vis can still
// hide them (totalsShown, below).
// Which flag wins is Looker's rule, read from its own table adapter
// (@looker/visualizations-adapters, configTransformations/showTotals.ts): an
// explicit `show_totals` decides; only without one does `hide_totals` apply
// (show = !hide_totals). A grid often carries BOTH — a `hide_totals: true` left
// over from the legacy table beside `show_totals: true` — and Looker shows the
// totals; reading "either one says hide" hid them in Pulse.
export function totalsShown(visConfig = {}, kind = 'total') {
  const show = kind === 'row' ? visConfig.show_row_totals : visConfig.show_totals;
  const hide = kind === 'row' ? visConfig.hide_row_totals : visConfig.hide_totals;
  if (typeof show === 'boolean') return show;
  return hide !== true;
}

export function hasColumnTotals(data, visConfig = {}) {
  if (!totalsShown(visConfig || {}, 'total')) return false;
  const t = data?.totals_data;
  return !!t && typeof t === 'object' && !Array.isArray(t) && Object.keys(t).length > 0;
}

// The total for one field — `pivotKey` picks a pivoted column (or ROW_TOTAL_KEY
// for the grand total). Returns null when that field has no total (dimensions,
// and table calcs Looker can't total), so the cell renders blank rather than "∅".
export function columnTotal(data, name, pivotKey = null) {
  const t = data?.totals_data?.[name];
  if (!t || typeof t !== 'object') return null;
  if (pivotKey) return isLeafCell(t[pivotKey]) ? t[pivotKey] : null;
  return isLeafCell(t) ? t : null;
}

// Row totals only exist on a pivoted query; detect them from the data itself so
// a stale `row_total` flag can't produce an empty column.
export function hasRowTotals(data, visConfig = {}) {
  if (!totalsShown(visConfig || {}, 'row')) return false;
  for (const row of data?.data || []) {
    for (const cell of Object.values(row || {})) {
      if (cell && typeof cell === 'object' && !isLeafCell(cell) && isLeafCell(cell[ROW_TOTAL_KEY])) return true;
    }
  }
  return false;
}

// Which measures actually carry a row total (so the extra column only spans the
// ones with numbers in it).
export function rowTotalMeasures(data, measures = []) {
  const rows = data?.data || [];
  return measures.filter((m) => rows.some((r) => rowTotal(r, m.name)));
}

export function rowTotal(row, name) {
  const c = row?.[name]?.[ROW_TOTAL_KEY];
  return isLeafCell(c) ? c : null;
}

// The genuine pivot COLUMNS — a pivoted query's row-total rides along as an
// extra entry in this same `pivots` array (is_total: true, key === ROW_TOTAL_KEY),
// not just as the ROW_TOTAL_KEY cell inside each row. Left in, it gets drawn as
// an ordinary column group ON TOP OF the dedicated totals column already built
// from rowTotalMeasures()/rowTotal() above — a duplicate, misplaced "Total" column.
export function realPivots(pivots = []) {
  return pivots.filter((p) => !p?.is_total && p?.key !== ROW_TOTAL_KEY);
}

// Totals cells print blank when absent — unlike a data cell, a missing total is
// "this column doesn't total", not "no value" (which cellText shows as ∅).
//
// Pass the FIELD the total belongs to. Looker renders the rows of a table but
// very often hands back `totals_data` with the raw number only — which is how a
// column of `1,234,567`s ended up sitting above a total reading `1234567`
// (issue #127). With a field, an unrendered number goes through that column's
// own value_format, so the total reads like the cells above it. Without one the
// value stays verbatim, exactly as before.
export function totalText(cell, field) {
  if (!cell) return '';
  if (cell.rendered != null && cell.rendered !== '') return cell.rendered;
  if (cell.value == null || cell.value === '') return '';
  if (field && isNumericValue(cell.value)) return formatNumber(cell.value, field.value_format);
  return String(cell.value);
}
